import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneFooter,
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import CreatorSearch from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/CreatorSearch";
import { ReactComponent as EraserIcon } from "src/images/eraser.svg";
import { customEqual } from "src/utils/methods";
import sortKeysDeep from "src/utils/sortKeysDeep";
import { areFiltersEmpty } from "../../utils";
import { changeSearchAudienceFilters } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/CreatorSearch/redux/creatorSearchActions";
import { useShowPane } from "src/hooks/useShowPane";

import "./CreatorSearchFilters.scss";

function areFiltersTheSame(filters, searchAudienceFilters) {
  const filtersSorted = sortKeysDeep(filters);
  const searchAudienceFiltersSorted = sortKeysDeep(searchAudienceFilters);

  return customEqual(filtersSorted, searchAudienceFiltersSorted);
}

function CreatorSearchFiltersFooter({
  clearFilters,
  applyFilters,
  showClearFiltersButton,
  showApplyFiltersButton,
}) {
  return (
    <PaneFooter>
      {showClearFiltersButton && (
        <Button
          variant="white-with-light-grey-border"
          size="large"
          onClick={clearFilters}
          data-qa="clear-filters-btn"
        >
          <EraserIcon />
          <IDHFormattedMessage id="ws_clear" defaultMessage="Clear" />
        </Button>
      )}
      {showApplyFiltersButton && (
        <Button
          variant="blue"
          size="large"
          data-qa="apply-changes-btn"
          onClick={applyFilters}
        >
          <IDHFormattedMessage
            id="ws_apply_filters"
            defaultMessage="Apply Filters"
          />
        </Button>
      )}
    </PaneFooter>
  );
}

export default function CreatorSearchFilters({
  closeFilters,
  filters,
  applyFilters,
}) {
  const [showPane, hidePane] = useShowPane(() => {
    closeFilters();
  });

  const dispatch = useDispatch();

  const { searchAudienceFilters } = useSelector(
    (state) => state.creatorSearchReducer,
  );

  const isFiltersMatchSavedState = areFiltersTheSame(
    filters,
    searchAudienceFilters,
  );

  const disableBackdropClick = !isFiltersMatchSavedState;
  const showClearFiltersButton = !areFiltersEmpty(searchAudienceFilters);
  const showApplyFiltersButton = !isFiltersMatchSavedState;

  const renderFooter = (clearFilters, applyFilters) => {
    if (!showClearFiltersButton && !showApplyFiltersButton) return null;
    return (
      <CreatorSearchFiltersFooter
        clearFilters={clearFilters}
        applyFilters={() => {
          applyFilters();
          hidePane();
        }}
        showClearFiltersButton={showClearFiltersButton}
        showApplyFiltersButton={showApplyFiltersButton}
      />
    );
  };

  const handleApplyFilters = (filters, shouldResetSuggestedFilters) => {
    applyFilters(filters, shouldResetSuggestedFilters);
    hidePane();
  };

  useEffect(() => {
    return () => {
      dispatch(changeSearchAudienceFilters({}));
    };
  }, [dispatch]);

  return (
    <>
      <Pane
        showPane={showPane}
        hidePane={hidePane}
        width={500}
        className="creator-search-filters"
        disableBackdropClick={disableBackdropClick}
      >
        <PaneTopbar>
          <PaneTopbarLeft>
            <FilterIcon className="filters-header__icon" />
            <span className="filters-header__title">
              <IDHFormattedMessage id="ws_filters" defaultMessage="Filters" />
            </span>
          </PaneTopbarLeft>
          <PaneTopbarToolbar hidePane={hidePane} />
        </PaneTopbar>
        <CreatorSearch
          preSelect={filters}
          applyFilters={handleApplyFilters}
          footer={renderFooter}
        />
      </Pane>
    </>
  );
}
